import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicOnlineAuctionView } from '../../../services/tournamentService';
import AuctionHeroHeader from '../../../components/auction/public/AuctionHeroHeader';
import AuctionSummaryCards from '../../../components/auction/public/AuctionSummaryCards';
import AuctionCurrentPlayerCard from '../../../components/auction/public/AuctionCurrentPlayerCard';
import AuctionPlayersTable from '../../../components/auction/public/AuctionPlayersTable';
import AuctionTeamsGrid from '../../../components/auction/public/AuctionTeamsGrid';
import AuctionOverviewPanels from '../../../components/auction/public/AuctionOverviewPanels';
import { usePublicAuctionView } from '../../../modules/auction-core/hooks/usePublicAuctionView';
import { getAuctionLifecycleNotice } from '../../../components/auction/public/AuctionSharedUtils';

import './public-auction.css';

const POLL_INTERVAL_MS = 7000;

const youtubeEmbedUrl = (url = '') => {
  const text = String(url || '').trim();
  if (!text) return '';
  const fromWatch = text.match(/[?&]v=([^&]+)/i)?.[1] || '';
  const fromShort = text.match(/youtu\.be\/([^?&/]+)/i)?.[1] || '';
  const fromEmbed = text.match(/embed\/([^?&/]+)/i)?.[1] || '';
  const videoId = fromWatch || fromShort || fromEmbed;
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
};

const PublicOnlineAuctionPage = () => {
  const { tournamentId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [playerTab, setPlayerTab] = useState('all');
  const { data, loading, error, auction, teams, players, soldPlayers, lastUpdatedAt } = usePublicAuctionView({
    tournamentId,
    fetcher: fetchPublicOnlineAuctionView,
    pollIntervalMs: POLL_INTERVAL_MS,
    errorMessage: 'Unable to load public online auction view.',
  });

  const auctionSettings = data?.auction?.settings || {};
  const playbackUrl = useMemo(
    () =>
      [auctionSettings.youtubePlaybackUrl, auctionSettings.streamUrl, data?.streaming?.watchLiveUrl]
        .find((url) => typeof url === 'string' && url.trim()) || '',
    [auctionSettings.streamUrl, auctionSettings.youtubePlaybackUrl, data?.streaming?.watchLiveUrl],
  );
  const liveStreamingUrl = useMemo(() => [auction.liveStreamingUrl, auction.youtubeLiveLink, auction.playbackUrl, playbackUrl, data?.liveStreamingUrl].find((url) => typeof url === 'string' && url.trim()) || null, [auction.liveStreamingUrl, auction.youtubeLiveLink, auction.playbackUrl, playbackUrl, data?.liveStreamingUrl]);
  const embedUrl = useMemo(() => youtubeEmbedUrl(playbackUrl), [playbackUrl]);
  const isStreamLive = Boolean(auctionSettings.isLive);
  const lifecycleNotice = getAuctionLifecycleNotice(auction.status);

  if (loading) return <div className="public-auction-view"><div className="card border-0 shadow-sm rounded-4"><div className="card-body p-4">Loading online auction view...</div></div></div>;
  if (error) return <div className="public-auction-view"><div className="alert alert-danger rounded-4">{error}</div></div>;

  return (
    <div className="public-auction-view">
      <section className="pav-live-stream">
        {isStreamLive && embedUrl ? (
          <div className="ratio ratio-16x9 rounded overflow-hidden shadow-sm border">
            <iframe
              src={embedUrl}
              title="Live online auction stream"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : lifecycleNotice && auction.status !== 'completed' ? (
          <div className={`alert alert-${lifecycleNotice.tone} mb-0`}>{lifecycleNotice.text}</div>
        ) : null}
      </section>
      <AuctionHeroHeader dashboard auction={auction} lastUpdatedAt={lastUpdatedAt} pollIntervalMs={POLL_INTERVAL_MS} liveStreamingUrl={liveStreamingUrl} />
      <AuctionSummaryCards dashboard auction={auction} players={players} teams={teams} />
      {auction.status === 'completed' ? <section className="pav-completed"><span aria-hidden="true">✓</span><div><h2>Auction Completed</h2><p>The auction has been completed. View the final results, player list and team squads below.</p></div></section> : <AuctionCurrentPlayerCard auction={auction} currentPlayer={auction.currentPlayer || data?.currentPlayer || null} />}

      <div className="pav-tabs" aria-label="Auction views">{['overview', 'players', 'teams'].map((tab) => <button key={tab} type="button" className="pav-pill" aria-pressed={activeTab === tab} onClick={() => setActiveTab(tab)}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>)}</div>
      {activeTab === 'overview' ? <AuctionOverviewPanels dashboard soldPlayers={soldPlayers} teams={teams} /> : null}
      {activeTab === 'players' ? <AuctionPlayersTable dashboard players={players} playerTab={playerTab} onTabChange={setPlayerTab} /> : null}
      {activeTab === 'teams' ? <AuctionTeamsGrid dashboard teams={teams} tournamentName={data?.tournament?.name || auction.name} /> : null}
    </div>
  );
};

export default PublicOnlineAuctionPage;
